import { useCallback, useEffect, useState } from 'react';
import {
  fetchPendingReleaseNote,
  dismissReleaseNote,
  type PendingReleaseNote,
} from '../services/releaseNotes.service';

/**
 * Fetches the one release note the current user should see on this login, if any.
 *
 * Runs once on mount — it deliberately does not poll. A note published mid-session
 * shows on the user's next visit rather than interrupting whatever they're doing.
 */
export function usePendingReleaseNote(enabled: boolean) {
  const [note, setNote] = useState<PendingReleaseNote | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetchPendingReleaseNote();
        if (cancelled || !res?.data) return;
        setNote(res.data);
        setIsOpen(true);
      } catch {
        // A failed lookup must never block the app — the user simply sees no modal.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const dismiss = useCallback(async () => {
    // Close first so the modal never feels laggy; the write is fire-and-forget.
    setIsOpen(false);
    if (!note) return;
    try {
      await dismissReleaseNote(note._id);
    } catch {
      // If this fails the note reappears next login — acceptable, and better
      // than surfacing an error for a dismissal.
    }
  }, [note]);

  return { note, isOpen, dismiss };
}
